import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Close, Share } from '@material-ui/icons';

import * as QPlayerSVGElement from '../../QPlayerSVGElement';
import { setOpenSharingModal } from '../../../actions/configurationActions';
import { getPreviewLink, joinClassNames } from '../../../util/util';
import ControlButton from '../ControlButton';
import tinycolor from 'tinycolor2';
import { MessageHelper } from '../../../util/message-helper';

interface SideControlsProps {
	settings: any;
	children?: React.ReactNode;
	isInlinePlayer?: boolean;
	isMobile?: boolean;
	queryParams?: any;
	className?: string;
	playerHover?: boolean;
	onMouseEnter?: (_e: React.MouseEvent) => void;
	onMouseLeave?: (_e: React.MouseEvent) => void;
}

export const SideControls = ({ settings, queryParams, isMobile, className }: SideControlsProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { currentVolumeLevel } = useSelector((state) => (state as any).playerReducer);
	const { mediaName, currentMediaId, accountId, mediasData, playlistId, embedShareSettings, mediaType, scenario } = useSelector(
		(state) => (state as any).configurationReducer
	);

	const enabledTopControlByPreset = settings?.controls?.enabled;
	const { sharing, inline } = queryParams ?? {};
	const showShareButton = !sharing ? enabledTopControlByPreset && settings?.controls?.showSocialSharing : sharing === 'true';
	const currentPlayerId = `player-${currentMediaId}`;
	let volumeBeforeMute = 0.5;

    const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
    const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
    const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
    const hoverStyle = { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
    const solidColor = settings?.controls?.solidBackgroundColor ?? '#000000';
    const solidColorStyle = settings?.controls?.showSolidBackgroundControl
        ? {
                backgroundColor: tinycolor({
                    ...tinycolor(solidColor).toRgb(),
                    a: Number(settings?.controls?.solidBackgroundControlOpacity ?? 100) / 100,
                }).toRgbString(),
            }
        : {};
    const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);

	const getVolumePercentage = useCallback((value: number) => {
		let result = Math.round(value * 100);
		return result;
	}, []);

	const handleButtonMute = () => {
		volumeBeforeMute = currentVolumeLevel;
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0);
	};

	const handleButtonUnmute = () => {
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(volumeBeforeMute);
	};

	const handleClosePlayerModal = () => {
		MessageHelper.sendMessageToLoader({
			type: MessageHelper.MESSAGE_TYPES.closePlayer,
			mediaId: playlistId || currentMediaId,
			scenarioId: scenario?.id,
			metadata: {},
		});
	};

	const handleShare = useCallback(() => {
		if (isMobile) {
			const paramConfigs = queryString.parse(window.location.search);
			const params = {
				mediaDetails: {
					id: mediaType === 'scenario' ? paramConfigs.scenarioId : currentMedia.id,
					title: mediaName || currentMedia?.mediaName,
				},
				configs: { id: paramConfigs.configId, ...settings },
				paramConfigs,
				accountId: accountId,
				type: mediaType,
				time: undefined,
				embedShareSettings: embedShareSettings,
			};
			navigator.share && navigator.share({ url: getPreviewLink({ ...params }) });
			return;
		}
		dispatch(setOpenSharingModal(true));
	}, [isMobile, dispatch, mediaType, mediaName, currentMedia?.mediaName, settings, accountId, embedShareSettings]);

	return (
		<div className={joinClassNames('controlsOverlaySideControls', className)}>
			<ControlButton
				className="controlsOverlaySideControls__close-btn"
				icon={<Close htmlColor="#fff" />}
				tabIndex={0}
				ariaLabel={t('LABEL_CLOSE')}
				// tooltip={t('LABEL_CLOSE')}
				onClick={handleClosePlayerModal}
				style={inline === 'off' ? { ...solidColorStyle, visibility: 'visible' } : { ...solidColorStyle, visibility: 'hidden' }}
				hoverStyle={hoverStyle}
			/>

			{settings?.controls.showAudioControls && (
				<ControlButton
					className="controlsOverlaySideControls__volume-btn"
					tabIndex={0}
					role={'button'}
					// tooltip={`${t('MUTE_BUTTON')}/${t('UNMUTE_BUTTON')}`}
					aria-valuetext={`${t('VOLUME')} ${getVolumePercentage(currentVolumeLevel)}%`}
					aria-label={`${currentVolumeLevel !== 0 ? t('MUTE_BUTTON') : t('UNMUTE_BUTTON')}, ${t('VOLUME_SLIDER_MOBILE')}`}
					onClick={currentVolumeLevel !== 0 ? handleButtonMute : handleButtonUnmute}
					icon={
						currentVolumeLevel === 0 ? (
							<QPlayerSVGElement.Unmute />
						) : currentVolumeLevel < 0.5 ? (
							<QPlayerSVGElement.VolumeSmall />
						) : (
							<QPlayerSVGElement.Mute />
						)
					}
					hoverStyle={hoverStyle}
					style={solidColorStyle}
				/>
			)}

			{showShareButton && (
				<ControlButton
					className="controlsOverlaySideControls__share-btn"
					icon={<Share htmlColor="#fff" />}
					tabIndex={0}
					ariaLabel={t('SHARE')}
					// tooltip={t('SHARE_TOOLTIP')}
					onClick={handleShare}
					hoverStyle={hoverStyle}
					style={solidColorStyle}
				/>
			)}
		</div>
	);
};
